<template>
  <div
    ref="cont"
    class="expand-card expndcrd no-scroll-track"
    :class="[
      { expndcrd_open: isOpen },
      {
        expndcrd_vertical_minify_active: verticalMinified || l_verticalMinified,
      },
      { expndcrd_closed: closed },
    ]"
    :style="floating ? 'position:fixed;z-index: 99' : ''"
  >
    <div
      class="expndcrd_title text-nowrap text-noselect"
      :class="[
        { 'expndcrd_title_no-close': !canOpen || (isOpen && !canClose) },
        { expndcrd_title_open: isOpen },
      ]"
      @click="toggle()"
    >
      <div
        v-if="icon"
        class="expndcrd_title_icon mdi text-large"
        :class="[icon, { csst: closed }]"
      >
        <div
          class="csst_content text-nowrap clr-white"
          style="
            position: fixed;
            margin: var(--tooltip-margin);
            transform: var(--tooltip-transform);
          "
          v-html="closedTooltip"
        ></div>
      </div>

      <slot
        name="title"
        :methods="{ setOpen }"
      >
        {{ title }}
      </slot>

      <div
        v-if="canClose && hasCloseIcon"
        data-tst="expndcrd_opener"
        class="mdi icon_btn transition_03 expndcrd_title_icon_opener csst csst_nm"
        :class="[
          openerProps.icon,
          {
            [openerProps.rotatedClass]: isOpen,
          },
        ]"
      >
        <div
          class="csst_content clr-white"
          :style="openerProps.tooltipStyle"
        >
          {{ l_open ? $t('btn.collapse') : $t('btn.expand') }}
        </div>
      </div>
    </div>
    <div
      class="expndcrd_content no-scroll-track"
      :class="{ expndcrd_content_open: isOpen }"
    >
      <slot
        name="default"
        :open="isOpen"
        :verticalMinified="verticalMinified || l_verticalMinified"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'ExpandCard',
  // mixins: [float_mix],
  props: {
    // openOnly: { type: Boolean,  default: true },
    canOpen: { type: Boolean, default: true },
    canClose: { type: Boolean, default: true },
    opened: { type: Boolean, default: true },
    closed: Boolean,
    persistKey: { default: '' },
    storeKey: { default: '' },
    hasCloseIcon: { type: Boolean, default: true },
    hasScrollIntoView: { type: Boolean, default: true },
    hasVerticalMinify: Boolean,
    verticalMinified: Boolean,

    icon: String,
    title: String,
    closedTooltip: String,
    id: [String, Number],

    scrollViewProps: {
      default: () => ({
        behavior: 'smooth',
        block: 'end',
        inline: 'end',
      }),
    },

    openerProps: {
      default: () => ({
        icon: 'mdi-chevron-down',
        rotatedClass: 'rotated_180_before',
        tooltipStyle: 'left: -0.25rem; transform: translate(-100%, 0)',
      }),
    },
  },
  emits: ['update:open'],
  data() {
    return {
      l_open: false,
      l_verticalMinified: false,

      floating: false,
      dragging: false,
    }
  },
  created() {
    let l_open = !this.canClose ? true : this.opened
    if (this.persistKey && this.canClose) {
      const lcValue = localStorage.getItem(this.persistKey)
      l_open = lcValue ? lcValue === 'open' : this.opened
    }
    this.setOpen(l_open, false)
  },
  methods: {
    setOpen(bool = false, emit = true) {
      if (!this.canOpen) return
      if (this.isOpen === bool) return

      this.l_open = bool
      if (this.hasVerticalMinify && bool) {
        this.l_verticalMinified = false
      }
      if (this.storeKey) {
        this.$store.commit('panels_set', { key: this.storeKey, value: bool })
      }
      if (emit) {
        this.$emit('update:open', this.l_open)
      }
    },
    close() {
      this.setOpen(false)
    },
    open() {
      this.setOpen(true)
    },
    handleWatcherOpened(bool) {
      if (!this.canClose && !bool) return
      this.setOpen(bool, false)
    },
    performVisibilityCheck(enoughPercent = 0.5) {
      const { cont } = this.$refs
      if (cont) {
        const observer = new IntersectionObserver(
          entries => {
            if (entries[0].intersectionRatio < enoughPercent) {
              this.scrollToContent(0)
            } else {
              this.toggle({ scrollIntoView: false })
            }
            observer.unobserve(cont)
          },
          {
            // root,
            rootMargin: '0px',
            threshold: 1,
          },
        )
        observer.observe(cont)
      }
    },
    toggle({ scrollIntoView = this.hasScrollIntoView } = {}) {
      if (this.dragging) return
      if (!this.canClose) return

      if (scrollIntoView && this.l_open) {
        this.performVisibilityCheck()
        // is open but not fully visible - so just scroll to it
        return
      }

      const l_open = this.closed
        ? true
        : !(this.storeKey ? this.$store.getters[this.storeKey] : this.l_open)
      this.setOpen(l_open)
      if (l_open) {
        this.scrollToContent(222)
      }

      if (this.persistKey) {
        if (this.l_open) {
          localStorage.setItem(this.persistKey, 'open')
        } else {
          localStorage.setItem(this.persistKey, 'closed')
        }
      }
    },
    scrollToContent(timeout) {
      if (!this.l_open) return
      if (!this.hasScrollIntoView) return
      if (!this.$refs.cont) return
      if (timeout) {
        setTimeout(() => {
          this.$refs.cont.scrollIntoView({
            behavior: 'smooth',
            ...this.scrollViewProps,
          })
        }, timeout)
      } else {
        this.$refs.cont.scrollIntoView({
          behavior: 'smooth',
          ...this.scrollViewProps,
        })
      }
    },
  },
  computed: {
    isOpen() {
      return (
        this.verticalMinified ||
        this.l_verticalMinified ||
        ((this.storeKey ? this.$store.getters[this.storeKey] : this.l_open) && !this.closed)
      )
    },
  },
  watch: {
    opened: {
      handler: 'handleWatcherOpened',
      immediate: false,
    },
  },
}
</script>
<style lang="scss">
// .expndcrd_closed {
//   .expndcrd_title {
//     .csst_content {
//       display: none;
//     }
//   }
// }
</style>
<style lang="scss" scoped>
.expndcrd {
  // box-shadow: 0px 0px 4px var(--clr-black-0);

  --radius-open: 0px;
  --radius-closed: 0px;
  --radius: var(--radius-open);

  --radius-title: 0px;
  --radius-content: 0px;
  // --border-content: none;
  --title-size: 1rem;
  --title-weight: 500;
  --title-padding: 0 0.5rem 0 0.75rem;
  --title-sticky-top: 0px;
  --title-color: var(--clr-grey-2);
  --title-direction: row;

  --transition: 0.3s;
  --transition-content: 1s;

  --opener-size: 1.5rem;
  --opener-color: var(--clr-grey-2);
  --clr-icon: inherit;

  --closed-opacity: 0;
  --open-opacity: 1;
  --width: 100%;
  --min-height: 2.5rem;
  --max-height: var(--vh-7-5);
  --content-padding: 1rem 1rem 1.5rem 1rem;
  // --text-transform: uppercase;
  --text-transform: none;

  --open-overflow: hidden;
  --overflow-content-open-y: auto;
  --overflow-content-open-x: hidden;

  --content-display: block;

  // --tooltip-transform: translate(0, -100%);
  // --tooltip-margin: 0.325rem 0 0 -3.25rem;
  --tooltip-transform: translate(0, 0);
  --tooltip-margin: -1.875rem 0 0 2.5rem;

  --clr-back-title: var(--clr-black-1-3);
  --clr-back-title-hover: var(--clr-black-1-3);
  --clr-back-title-active: var(--clr-black-1-3);

  --clr-back-content: var(--clr-black-1);
  // --clr-back-content: var(--clr-black-1-1);

  // --opener-opacity: 0;
  --opener-opacity: 1;
  --opener-opacity-hover: 1;
  --icon-opener-margin: 0 0 0 auto;
  --icon-opener-padding: 0.125rem;

  --dragdrop-opacity: 1;
  --dragdrop-opacity-hover: 1;
  --dragdrop-inset: 0.365rem 2.75rem auto auto;
  --dragdrop-size: 1.25rem;

  --vrm-icon-opacity: 1;
  --vrm-icon-opacity-hover: 1;
  --vrm-icon-inset: 0.365rem 4.75rem auto auto;
  --vrm-icon-size: 1.25rem;
  // --icon-dragdrop-margin: 0 0 0 auto;

  --title-align-items: flex-start;
  --title-justify-content: flex-start;
  // --title-justify-content: space-between;
  // --pos-sticky-top: 0;

  // min-width: var(--min-width);
  max-height: var(--min-height);
  width: var(--width);
  background: var(--clr-back);
  border-radius: var(--radius);
  transition: max-height var(--transition);
  // position: sticky;
  position: relative;
  // top: var(--pos-sticky-top);
  overflow: hidden;
  flex-shrink: 0;
  // width: 100%;

  // &_dragdrop {
  //   // margin: var(--icon-dragdrop-margin);
  //   padding: 0.25rem;
  //   opacity: var(--dragdrop-opacity);
  //   font-size: var(--dragdrop-size);
  //   color: var(--dragdrop-color);
  //   font-size: var(--dragdrop-size);

  //   position: absolute;
  //   inset: var(--dragdrop-inset);
  //   color: var(--clr-grey-2);
  //   padding: 0.25rem;
  //   transition: opacity 0.22s;
  // }
  &_title {
    // -webkit-touch-callout: none; /* iOS Safari */
    // -webkit-user-select: none; /* Safari */
    // user-select: none;
    flex-shrink: 0;
    color: var(--title-color);
    background: var(--clr-back);
    display: flex;
    align-items: center;
    // justify-content: space-between;
    flex-direction: var(--title-direction);
    // align-items: var(--title-align-items);
    justify-content: var(--title-justify-content);
    padding: var(--title-padding);
    width: 100%;
    cursor: pointer;
    transition: var(--transition);
    min-height: var(--min-height);
    top: var(--title-sticky-top);
    bottom: 0;
    font-size: var(--title-size);
    font-weight: var(--title-weight);
    text-transform: var(--text-transform);
    border-radius: var(--radius-title);
    background: var(--clr-back-title);
    position: relative;

    &_no-close {
      cursor: default;
    }
    &_icon {
      margin: 0 0.75rem 0 0;
      color: var(--clr-icon);
      &_opener {
        margin: var(--icon-opener-margin);
        padding: var(--icon-opener-padding);
        opacity: var(--opener-opacity);
        font-size: var(--opener-size);
        color: var(--opener-color);
      }
    }
    // &_tooltip {
    // }
    &_open {
      --clr-back-title: var(--clr-back-title-active);
    }
  }
  &_closed {
    transition: 0s !important;
    --radius: var(--radius-closed);
    // .expndcrd_title {
    //   .csst_content {
    //     display: none;
    //   }
    // }
  }
  &_open {
    overflow: var(--open-overflow);
    max-height: var(--max-height);
  }
  &_vertical_minify {
    &_active {
      --min-width: 3rem;
      --width: var(--min-width);
    }
    // &_icon {
    //   padding: 0.25rem;
    //   // opacity: var(--vrm-icon-opacity);
    //   font-size: var(--dragdrop-size);
    //   color: var(--vrm-icon-color);
    //   font-size: var(--vrm-icon-size);

    //   position: absolute;
    //   inset: var(--vrm-icon-inset);
    //   color: var(--clr-grey-2);
    //   padding: 0.25rem;
    //   transition: opacity 0.22s;
    // }
  }
  &_content {
    flex-shrink: 0;
    opacity: var(--closed-opacity);
    transition: var(--transition-content);
    padding: var(--content-padding);
    background: var(--clr-back-content);
    border-radius: var(--radius-content);
    display: var(--content-display);
    min-height: 0;
    // border: var(--border-content);
    flex: 1;

    &_open {
      opacity: var(--open-opacity);
      transition: var(--transition);
      max-height: calc(var(--max-height) - var(--min-height));
      overflow-y: var(--overflow-content-open-y);
      overflow-x: var(--overflow-content-open-x);

      // used for tree-preview when hSpots can overflow
      animation: overflowDelay var(--transition);
      @keyframes overflowDelay {
        0% {
          overflow-y: hidden;
          overflow-x: hidden;
        }
        100% {
          overflow-y: hidden;
          overflow-x: hidden;
        }
      }
    }
  }
}

@media (hover: hover) and (pointer: fine) {
  .expndcrd {
    --dragdrop-opacity: 0;
    // --vrm-icon-opacity: 0;
    &:hover {
      --dragdrop-opacity: 1;
      // --vrm-icon-opacity: 1;
    }
    &_title {
      &:not(.expndcrd_title_no-close) {
        &:hover {
          --dragdrop-opacity: 1;
          --clr-back-title: var(--clr-back-title-hover);
          --opener-opacity: var(--opener-opacity-hover);
        }
      }
    }
  }
}
</style>
